import { zodResolver } from '@hookform/resolvers/zod';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { forgotPassword } from '@/services/authService';
import { getLandingPrivacyUrl } from '@/utils/landingUrl';

export function ForgotPasswordForm({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  const { t, i18n } = useTranslation();
  const privacyUrl = getLandingPrivacyUrl(i18n.language ?? i18n.resolvedLanguage ?? 'en');
  const forgotPasswordSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('validation.emailInvalid')),
      }),
    [t],
  );
  type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: ForgotPasswordFormValues): Promise<void> => {
    try {
      await forgotPassword(values.email);
      toast.success(t('auth.forgotPassword.success'));
    } catch (error) {
      toast.error(t('auth.forgotPassword.error'));
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-2 flex-col">
                <h1 className="text-2xl font-bold">
                  {t('auth.forgotPassword.title')}
                </h1>
                <p className="text-balance text-muted-foreground">
                  {t('auth.forgotPassword.description')}
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t('auth.forgotPassword.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  {...register('email')}
                />
                {errors.email ? (
                  <p className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                ) : null}
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting
                  ? t('auth.forgotPassword.submitting')
                  : t('auth.forgotPassword.submit')}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to="/login"
                  className="underline underline-offset-4 text-primary"
                >
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </form>
          <div className="hidden items-center justify-center bg-muted p-8 md:flex">
            <img
              src="/brand/trushelf-horizontal.svg"
              alt="TruShelf"
              className="w-2/3 max-w-xs"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-white/90 [&_a]:underline [&_a]:underline-offset-4 [&_a]:text-white [&_a]:hover:text-secondary">
        {t('auth.login.termsIntro')}{' '}
        <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
          {t('auth.login.privacy')}
        </a>
        .
      </div>
    </div>
  );
}

export function ForgotPasswordPage(): JSX.Element {
  return (
    <div className="bg-primary flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <ForgotPasswordForm />
      </div>
    </div>
  );
}
